import type { DepositTerms, PlanningItem, Quote, RecordQuoteSchema } from '@peles/shared';

/** The words an item or a line is known by, for matching one to the other. */
function words(text: string): Set<string> {
  return new Set(
    text
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter((w) => w.length > 1),
  );
}

export function itemLabel(item: Pick<PlanningItem, 'itemName' | 'catalogItemName'>): string {
  return item.itemName || item.catalogItemName || 'Untitled item';
}

/**
 * Which item each line a vendor wrote prices — a proposal the producer
 * confirms, never applied unseen. A line goes to the item sharing the most of
 * its words (at least half the item's), each item taken once; the rest are
 * extras.
 */
export function matchLinesToItems(
  lines: { description: string }[],
  items: Pick<PlanningItem, 'id' | 'itemName' | 'catalogItemName'>[],
): (string | null)[] {
  const itemWords = items.map((i) => ({ id: i.id, words: words(itemLabel(i)) }));
  const taken = new Set<string>();
  return lines.map((line) => {
    const lineWords = words(line.description);
    let best: { id: string; score: number } | null = null;
    for (const item of itemWords) {
      if (taken.has(item.id) || item.words.size === 0) continue;
      let shared = 0;
      for (const w of item.words) if (lineWords.has(w)) shared++;
      const score = shared / item.words.size;
      if (score >= 0.5 && (!best || score > best.score)) best = { id: item.id, score };
    }
    if (best) taken.add(best.id);
    return best?.id ?? null;
  });
}

export interface ExtraLineDraft {
  key: string;
  description: string;
  quantity: string;
  unitPrice: string;
}

export interface QuoteDraft {
  total: string;
  /** Unit price per item id, as typed. */
  itemPrices: Record<string, string>;
  extras: ExtraLineDraft[];
  depositKind: 'none' | 'percent' | 'amount';
  depositValue: string;
  depositDue: 'signing' | 'date';
  depositDate: string;
  validUntil: string;
  notes: string;
  documentIds: string[];
}

let extraSeq = 0;
export function newExtra(over: Partial<ExtraLineDraft> = {}): ExtraLineDraft {
  extraSeq += 1;
  return { key: `extra-${extraSeq}`, description: '', quantity: '1', unitPrice: '', ...over };
}

/** A blank draft, or the quote's current figures to revise. */
export function draftFromQuote(quote: Quote | null): QuoteDraft {
  const terms = quote?.depositTerms ?? null;
  const itemPrices: Record<string, string> = {};
  const extras: ExtraLineDraft[] = [];
  for (const line of quote?.lineItems ?? []) {
    if (line.planningItemId) itemPrices[line.planningItemId] = String(line.unitPrice);
    else
      extras.push(
        newExtra({
          description: line.description,
          quantity: String(line.quantity),
          unitPrice: String(line.unitPrice),
        }),
      );
  }
  return {
    total: quote?.totalAmount != null ? String(quote.totalAmount) : '',
    itemPrices,
    extras,
    depositKind:
      terms?.percent !== undefined ? 'percent' : terms?.amount !== undefined ? 'amount' : 'none',
    depositValue:
      terms?.percent !== undefined
        ? String(terms.percent)
        : terms?.amount !== undefined
          ? String(terms.amount)
          : '',
    depositDue: terms && terms.due !== 'signing' ? 'date' : 'signing',
    depositDate: terms && terms.due !== 'signing' ? terms.due : '',
    validUntil: quote?.validUntil ?? '',
    notes: quote?.notes ?? '',
    documentIds: [],
  };
}

function num(text: string): number | null {
  if (text.trim() === '') return null;
  const v = Number(text);
  return Number.isFinite(v) ? v : NaN;
}

/**
 * The figures to send, or the first thing wrong with the draft in words the
 * producer can act on. A total left blank is the sum of the lines (the server
 * adds them); nothing at all is refused.
 */
export function draftToFigures(
  draft: QuoteDraft,
  items: Pick<PlanningItem, 'id' | 'itemName' | 'catalogItemName' | 'quantity'>[],
): { figures: RecordQuoteSchema } | { error: string } {
  const lineItems: NonNullable<RecordQuoteSchema['lineItems']> = [];
  for (const item of items) {
    const price = num(draft.itemPrices[item.id] ?? '');
    if (price === null) continue;
    if (Number.isNaN(price) || price < 0)
      return { error: `The price for ${itemLabel(item)} is not a number.` };
    lineItems.push({
      planningItemId: item.id,
      description: itemLabel(item),
      quantity: item.quantity > 0 ? item.quantity : 1,
      unitPrice: price,
    });
  }
  for (const extra of draft.extras) {
    const price = num(extra.unitPrice);
    const qty = num(extra.quantity);
    if (!extra.description.trim() && price === null) continue;
    if (!extra.description.trim()) return { error: 'Every extra line needs a description.' };
    if (price === null || Number.isNaN(price) || price < 0)
      return { error: `The price for ${extra.description.trim()} is not a number.` };
    if (qty === null || Number.isNaN(qty) || qty <= 0)
      return { error: `The quantity for ${extra.description.trim()} must be more than 0.` };
    lineItems.push({
      planningItemId: null,
      description: extra.description.trim(),
      quantity: qty,
      unitPrice: price,
    });
  }

  const total = num(draft.total);
  if (total !== null && (Number.isNaN(total) || total < 0))
    return { error: 'The total is not a number.' };
  if (total === null && lineItems.length === 0)
    return { error: 'Enter a total or a price for at least one item.' };

  let depositTerms: DepositTerms | null = null;
  if (draft.depositKind !== 'none') {
    const value = num(draft.depositValue);
    if (value === null || Number.isNaN(value) || value <= 0)
      return { error: 'Enter the deposit, or choose no deposit.' };
    if (draft.depositKind === 'percent' && value > 100)
      return { error: 'A deposit cannot be more than 100%.' };
    if (draft.depositDue === 'date' && !draft.depositDate)
      return { error: 'Choose the day the deposit is due.' };
    depositTerms = {
      ...(draft.depositKind === 'percent' ? { percent: value } : { amount: value }),
      due: draft.depositDue === 'signing' ? 'signing' : draft.depositDate,
    };
  }

  return {
    figures: {
      // Left blank, the server adds up the lines.
      ...(total !== null ? { totalAmount: total } : {}),
      lineItems,
      depositTerms,
      validUntil: draft.validUntil || null,
      notes: draft.notes.trim() || null,
      ...(draft.documentIds.length ? { documentIds: draft.documentIds } : {}),
    },
  };
}
